import { useMemo } from 'react';
import {
  ANALYST_PRICE_TARGETS,
  COMPANY_DIVIDEND_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  VALUATION_PE_RATIO,
  VALUATION_PRICE_VS_FAIR_RATIO,
  VALUATION_RATIO_VS_PEERS,
} from '@/constants/anchors';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import { slugify } from '@/utilities/slugify';

function useSummaryRewards() {
  const canonicalURL = useCompanyCanonicalUrl();
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL,
  });
  const rewardStatements = useMemo(() => {
    if (!statements) return [];
    const {
      IsGoodValue,
      IsGrowingProfitOrRevenue,
      HasBeenGrowingProfitOrRevenue,
      IsDividendAttractive,
      IsGoodRelativeValue,
      IsTradingBelowAnalystPriceTargets,
    } = statements;

    return [
      {
        statement: IsGoodValue,
        name: 'isGoodValue',
        index: 0,
        hash:
          IsGoodValue?.outcome_name === 'OUTCOME_TRUE'
            ? VALUATION_PRICE_VS_FAIR_RATIO
            : VALUATION_PE_RATIO,
        statementId: slugify(IsGoodValue?.title),
        description: IsGoodValue?.description,
      },

      {
        statement: IsGrowingProfitOrRevenue,
        name: 'isCompanyGrowing',
        index: 1,
        hash: COMPANY_FUTURE_ANCHOR,
        statementId: slugify(IsGrowingProfitOrRevenue?.title),
        description: IsGrowingProfitOrRevenue?.description,
      },

      {
        statement: HasBeenGrowingProfitOrRevenue,
        name: 'hasCompanyBeenGrowing',
        index: 2,
        hash: COMPANY_PAST_ANCHOR,
        statementId: slugify(HasBeenGrowingProfitOrRevenue?.title),
        description: HasBeenGrowingProfitOrRevenue?.description,
      },

      {
        statement: IsDividendAttractive,
        name: 'isDividendAttractive',
        index: 3,
        hash: COMPANY_DIVIDEND_ANCHOR,
        statementId: slugify(IsDividendAttractive?.title),
        description: IsDividendAttractive?.description,
      },
      {
        statement: IsGoodRelativeValue,
        name: 'isGoodRelativeValue',
        index: 4,
        hash: VALUATION_RATIO_VS_PEERS,
        statementId: IsGoodRelativeValue?.name,
        description: IsGoodRelativeValue?.description,
      },

      {
        statement: IsTradingBelowAnalystPriceTargets,
        name: 'isTradingBelowAnalystPriceTargets',
        index: 7,
        hash: ANALYST_PRICE_TARGETS,
        statementId: IsTradingBelowAnalystPriceTargets?.name,
        description: IsTradingBelowAnalystPriceTargets?.description,
      },
    ].filter(({ statement }) => statement?.value === true);
  }, [statements]);

  return rewardStatements;
}

export default useSummaryRewards;
